import React, { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { IIntegrationsContext, IntegrationsContext, SyncStatus } from './';
import useFeatureFlag from 'shared/hooks/useFeatureFlag';
import { FeatureFlagName, GraphQLFetchPolicies } from 'shared/enums';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { HStack } from '@televet/kibble-ui/build/chakra';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import {
  ClinicPimsIntegrationType,
  useGetMostRecentSyncDataLazyQuery,
  useSubscribeToSyncOutagesSubscription,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { useHistory } from 'react-router-dom';
import { SYNC_STATUS_HELP_ARTICLE_URL } from 'pages/Settings/pages/SyncStatusSettings';
import { useTracking } from 'shared/providers/TrackingProvider';
import { getHasPrescriptionCapabilities } from '../utils/getHasPrescriptionCapabilities';
import { parseISO, add, differenceInMilliseconds } from 'date-fns';
import { clinicWithinSyncWindow } from '../utils/timeFuncs';
import { useSupportedIntegrationFeatures } from '../hooks/useSupportedIntegrationFeatures';
import { getIntegrationDisplayName } from '../utils/getIntegrationDisplayName';
import { IntegrationSystemName } from '..';
import { SettingsSyncStatusEvents } from 'shared/providers/TrackingProvider/EventNames';

export const useIntegrationsProvider = (): IIntegrationsContext =>
  useContext<IIntegrationsContext>(IntegrationsContext);

interface IntegrationsProviderProps {
  children: ReactNode;
}

export const IntegrationsProvider = ({ children }: IntegrationsProviderProps): JSX.Element => {
  const { currentClinic } = useClinicUser();
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlag();
  const [hasSyncOutage, setHasSyncOutage] = useState(false);
  const [isSyncPaused, setIsSyncPaused] = useState(false);

  const history = useHistory();
  const toast = useToast();
  const { trackEvent } = useTracking();

  const allIntegrations = useMemo(() => {
    return currentClinic?.integrations || [];
  }, [currentClinic]);

  const allActiveIntegrations = useMemo(() => {
    return allIntegrations.filter((integration) => integration.isActive);
  }, [allIntegrations]);

  const primaryIntegration = useMemo(() => {
    // If clinic has only one active integration and it's Bitwerx, return it as the primary integration
    if (allActiveIntegrations.length === 1 && allActiveIntegrations[0].type === ClinicPimsIntegrationType.Bitwerx) {
      return allActiveIntegrations[0];
    }

    // Otherwise, filter out Bitwerx and PPC integrations in favor of any other integration in the list
    const filteredIntegrations = allActiveIntegrations.filter(
      ({ type }) => type !== ClinicPimsIntegrationType.Bitwerx && type !== ClinicPimsIntegrationType.Ppc,
    );
    const hasSyncVetIntegration = filteredIntegrations.find(({ type }) => type === ClinicPimsIntegrationType.SyncVet);

    // If clinic has SyncVet as well as another integration type, prefer the other integration over SyncVet
    if (hasSyncVetIntegration && filteredIntegrations.length > 1) {
      return filteredIntegrations.find(({ type }) => type !== ClinicPimsIntegrationType.SyncVet) || null;
    }

    return filteredIntegrations[0] || null;
  }, [allActiveIntegrations]);

  const primaryIntegrationSystemName: IntegrationSystemName | undefined | null = useMemo(() => {
    if (primaryIntegration?.vetdata) {
      return primaryIntegration.vetdata.system;
    }

    if (primaryIntegration?.syncvet) {
      return primaryIntegration.syncvet.system;
    }

    return;
  }, [primaryIntegration]);

  const primaryIntegrationDisplayName = useMemo(() => {
    return getIntegrationDisplayName(primaryIntegration?.type, primaryIntegrationSystemName);
  }, [primaryIntegration?.type, primaryIntegrationSystemName]);

  const {
    isPostcardsSupported,
    isBillingCodeSupported,
    isClientIdSupported,
    isPatientIdSupported,
    isInvoicePreviewSupported,
    isInvoiceDiscoverySupported,
    isContactSyncSupported,
    isMergeExistingPimsProfileSupported,
    isExportToPimsSupported,
    isServiceCodeSupported,
    isTriggerPimsSyncSupported,
    isWritebackApptNotesSupported,
    isAppointmentCreateWritebackSupported,
    isAppointmentEditWritebackSupported,
    isAppointmentRescheduleSupported,
    isServiceRemindersDisabledForEzyvet,
  } = useSupportedIntegrationFeatures(primaryIntegration?.type, primaryIntegrationSystemName, allActiveIntegrations);

  const hasOfficeHoursSyncFlag = useMemo(() => {
    return !isFeatureFlagsLoading && isFeatureEnabled(FeatureFlagName.OfficeHourSync);
  }, [isFeatureEnabled, isFeatureFlagsLoading]);

  const [getMostRecentSyncData, { data: syncData, refetch: refetchSyncData }] = useGetMostRecentSyncDataLazyQuery({
    variables: {
      where: {
        integrationId: {
          equals: primaryIntegration?.id,
        },
      },
    },
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    onCompleted: ({ findFirstIntegrationOutageHistoryEntry }) => {
      const syncError = findFirstIntegrationOutageHistoryEntry?.status === SyncStatus.SyncProblem;
      setHasSyncOutage(false);

      const isClinicInSyncWindow = clinicWithinSyncWindow(currentClinic);
      setIsSyncPaused(!syncError && !isClinicInSyncWindow && hasOfficeHoursSyncFlag);
      if (!syncError && !isClinicInSyncWindow && hasOfficeHoursSyncFlag) {
        if (toast.isActive('sync-status')) return;
        toast({
          title: `Syncs to your ${primaryIntegrationDisplayName} server are currently paused and will resume 1 hour before your clinic opens. You can force a refresh by visiting the link below.`,
          status: 'warning',
          id: 'sync-status',
          duration: 15000, // set to 1500 for demo, revert to shorter value
          children: (
            <HStack mt={2} spacing={1}>
              <Button
                size="xs"
                isOnContrast
                variant="secondary"
                onClick={(): void => {
                  trackEvent(SettingsSyncStatusEvents.ViewSettingsPageFromAlert);
                  history.push(`${RouteBasePaths.Settings}${SettingsRoutes.SyncStatus}`);
                  toast.close('sync-status');
                }}
              >
                View Sync Status Page
              </Button>
            </HStack>
          ),
        });
      }

      if (syncError) {
        const errorTimeStamp = parseISO(findFirstIntegrationOutageHistoryEntry?.createdAt);
        const fortyFiveMinuteDelayTimeStamp = add(errorTimeStamp, { minutes: 45 });
        const timeLeftInMilliseconds = differenceInMilliseconds(new Date(fortyFiveMinuteDelayTimeStamp), new Date());

        /*
         * Only show the outage notification when the outage lasts longer than 45 minutes
         * This should help to prevent unnecessary concern/confusion for clinics if the outage is resolved in a timely manner
         */
        setTimeout(
          () => {
            setHasSyncOutage(true);
            if (toast.isActive('sync-status')) return;
            trackEvent(SettingsSyncStatusEvents.SyncOutageOccurred);
            toast({
              title: `We are currently having issues communicating with your ${primaryIntegrationDisplayName} server`,
              status: 'warning',
              id: 'sync-status',
              duration: 3000,
              children: (
                <HStack mt={2} spacing={1}>
                  <Button
                    size="xs"
                    isOnContrast
                    variant="secondary"
                    onClick={(): void => {
                      trackEvent(SettingsSyncStatusEvents.ViewSettingsPageFromAlert);
                      history.push(`${RouteBasePaths.Settings}${SettingsRoutes.SyncStatus}`);
                    }}
                  >
                    View Sync Status
                  </Button>
                  <Button
                    size="xs"
                    isOnContrast
                    variant="secondary"
                    onClick={(): void => {
                      trackEvent(SettingsSyncStatusEvents.ViewKnowledgeBaseArticle, { source: 'Sync Status Alert' });
                      window.open(SYNC_STATUS_HELP_ARTICLE_URL);
                    }}
                  >
                    See Troubleshooting Steps
                  </Button>
                </HStack>
              ),
            });
          },
          timeLeftInMilliseconds >= 0 ? timeLeftInMilliseconds : 0,
        );
      }
    },
  });

  useSubscribeToSyncOutagesSubscription({
    variables: {
      where: {
        integrationId: {
          equals: primaryIntegration?.id,
        },
      },
    },
    skip: !primaryIntegration?.id,
    onData: () => {
      if (refetchSyncData) {
        refetchSyncData();
      }
    },
  });

  const syncStatus: SyncStatus = useMemo(
    (): SyncStatus => syncData?.findFirstIntegrationOutageHistoryEntry?.status as SyncStatus,
    [syncData],
  );

  const lastSyncTime = useMemo(() => {
    return syncData?.findFirstIntegrationOutageHistoryEntry?.createdAt || null;
  }, [syncData]);

  const hasPrescriptionCapabilities = useMemo(() => {
    return getHasPrescriptionCapabilities(allActiveIntegrations);
  }, [allActiveIntegrations]);

  const hasActiveBitwerxIntegration = useMemo(() => {
    return allActiveIntegrations.some((integration) => integration.type === ClinicPimsIntegrationType.Bitwerx);
  }, [allActiveIntegrations]);

  useEffect(() => {
    // Needed to let data populate fully after switching clinics
    setTimeout(() => {
      getMostRecentSyncData();
    }, 2000);
  }, [getMostRecentSyncData]);

  return (
    <IntegrationsContext.Provider
      value={{
        syncStatus: {
          hasSyncOutage,
          currentStatus: syncStatus,
          lastSyncTime,
          isSyncPaused,
          primaryIntegration,
          timezoneName: currentClinic?.timezoneName,
        },
        allIntegrations,
        allActiveIntegrations,
        primaryIntegration,
        primaryIntegrationName: primaryIntegrationDisplayName,
        isInvoicePreviewSupported: isInvoicePreviewSupported || false,
        isTriggerPimsSyncSupported: isTriggerPimsSyncSupported || false,
        isClientIdSupported: isClientIdSupported || false,
        isExportToPimsSupported: isExportToPimsSupported || false,
        isWritebackApptNotesSupported: isWritebackApptNotesSupported || false,
        isContactSyncSupported: isContactSyncSupported || false,
        isInvoiceDiscoverySupported: isInvoiceDiscoverySupported || false,
        isPostcardsSupported: isPostcardsSupported || false,
        isBillingCodeSupported: isBillingCodeSupported || false,
        isServiceCodeSupported: isServiceCodeSupported || false,
        isPatientIdSupported: isPatientIdSupported || false,
        isAppointmentRescheduleSupported: isAppointmentRescheduleSupported || false,
        isAppointmentCreateWritebackSupported: isAppointmentCreateWritebackSupported || false,
        isAppointmentEditWritebackSupported: isAppointmentEditWritebackSupported || false,
        isMergeExistingPimsProfileSupported: isMergeExistingPimsProfileSupported || false,
        hasPrescriptionCapabilities,
        isServiceRemindersDisabledForEzyvet,
        hasActiveBitwerxIntegration,
      }}
    >
      {children}
    </IntegrationsContext.Provider>
  );
};
